<script setup lang="ts">
  import { useProjectStore } from '@/store/project.store'
  import { createEmptyProject, type Project } from '@/types/nest/projects'
  import { createFlatBaseColumns } from '@/utils/table'
  import {
    Modal,
    Table,
    UiButton,
    FormInput,
    ConfirmationDialog,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref } from 'vue'
  import TopBar from '@/components/TopBar.vue'
  import { useRouter } from 'vue-router'
  import { useProjectValidation } from '@/composables/validation/use-project-validation'

  const MAX_PROJECT_NAME_LENGTH = 150

  const projectsStore = useProjectStore()
  const toastStore = useToastStore()
  const showProjectModal = ref(false)
  const projectToDelete = ref<Project | null>(null)

  const newProject = ref<Project>(createEmptyProject())
  const router = useRouter()
  const { validate, visibleErrors } = useProjectValidation(newProject)

  async function createProject() {
    if (!validate()) return

    try {
      await projectsStore.createProject(newProject.value)
      await projectsStore.fetchProjects()
      showProjectModal.value = false
      newProject.value = createEmptyProject()
      toastStore.success('Project succesvol aangemaakt')
    } catch (error) {
      toastStore.error('Error creating project:')
      throw error
    }
  }

  function openDeleteDialog(project: Project) {
    projectToDelete.value = project
  }

  async function deleteProject(id: string) {
    try {
      await projectsStore.deleteProject(id)
      projectToDelete.value = null
      toastStore.success('Project succesvol verwijderd')
    } catch (error) {
      toastStore.error('Error deleting project:')
      throw error
    }
  }

  onMounted(async () => {
    await projectsStore.fetchProjects()
  })

  function createFlatBaseColumnsForProjects() {
    const hasData = projectsStore.projects.length > 0
    const sampleRows = hasData ? projectsStore.projects : [createEmptyProject()]

    const allKeys = Object.keys([
      'id',
      'customerId',
      'name',
      'createdAt',
      'updatedAt',
      'deletedAt',
    ]) as (keyof Project)[]
    const selectedKeys: (keyof Project)[] = ['name', 'customerId', 'id']

    const columns = createFlatBaseColumns(
      sampleRows,
      hasData ? ['deletedAt', 'createdAt', 'updatedAt'] : [],
      {
        searchableKeys: hasData ? selectedKeys : allKeys,
        columnOrder: hasData ? selectedKeys : allKeys,
      }
    )

    if (hasData) {
      columns.push({ key: 'action', label: 'actie', sticky: 'right' })
    }

    return columns
  }

  async function navigate(projectId: string) {
    await router.push({
      name: 'project-details',
      params: { uuid: projectId },
    })
  }

  const showConfirmationDialog = computed({
    get: () => projectToDelete.value != null,
    set: value => {
      if (!value) projectToDelete.value = null
    },
  })
</script>

<template>
  <TopBar />
  <Modal v-model:show="showProjectModal">
    <template #header>
      <h3>Nieuw project</h3>
    </template>
    <FormInput
      v-model="newProject.name"
      label="Project naam"
      placeholder="Voer de project naam in"
      type="text"
      :error="visibleErrors && visibleErrors.name"
      :maxLength="MAX_PROJECT_NAME_LENGTH"
    />
    <template #footer>
      <UiButton label="Opslaan" variant="prominent" @click="createProject" />
    </template>
  </Modal>

  <div class="project-list-container">
    <Table
      v-show="projectsStore.projects.length > 0"
      :rows="projectsStore.projects"
      :columns="createFlatBaseColumnsForProjects()"
      :configurable="true"
      configuration-storage-key="ProjectListTableConfig"
      row-key="id"
    >
      <template #table-top-actions>
        <UiButton
          label="Nieuw project"
          variant="prominent"
          @click="showProjectModal = true"
          icon="plus"
        />
      </template>

      <template #cell-action="{ row }">
        <div class="action-row">
          <UiButton
            size="small"
            label="Details"
            variant="prominent"
            icon="chevronRight"
            @click="navigate(row.id)"
          />

          <UiButton size="small" variant="negative" @click="openDeleteDialog(row)" icon="delete" />
        </div>
      </template>
    </Table>

    <ConfirmationDialog
      :title="`Are you sure you want to remove: ${projectToDelete?.name}?`"
      body="This action cannot be undone."
      confirmButtonText="Remove"
      confirmButtonColor="negative"
      v-model:show="showConfirmationDialog"
      :confirm="() => projectToDelete && deleteProject(projectToDelete.id!)"
    />
  </div>
</template>

<style scoped lang="css">
  .project-list-container {
    padding: var(--spacing-m);
  }

  .action-row {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-s);
  }
</style>
